import React, { useEffect, useState, useCallback } from 'react';
import {
  Building2,
  Search,
  MoreVertical,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mail,
  Globe,
  Calendar,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ExternalLink,

} from 'lucide-react';
import api from '../api';

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    disabled: 0,
    suspended: 0
  });

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token not found.');
      const response = await api.get('api/auth/superadmin/tenants', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenants(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch tenants.');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = useCallback(() => {
    const active = tenants.filter(t => t.status === 'active').length;
    const disabled = tenants.filter(t => t.status === 'disabled').length;
    const suspended = tenants.filter(t => t.status === 'suspended').length;
    setStats({ total: tenants.length, active, disabled, suspended });
  }, [tenants]);

  const filterAndSortTenants = useCallback(() => {
    let filtered = [...tenants];
    if (searchQuery) {
      filtered = filtered.filter(tenant =>
        tenant.organizationName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tenant.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tenant.subdomain?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(tenant => tenant.status === statusFilter);
    }
    switch (sortBy) {
      case 'newest': filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); break;
      case 'oldest': filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)); break;
      case 'name-asc': filtered.sort((a, b) => (a.organizationName || '').localeCompare(b.organizationName || '')); break;
      case 'name-desc': filtered.sort((a, b) => (b.organizationName || '').localeCompare(a.organizationName || '')); break;
      default: break;
    }
    setFilteredTenants(filtered);
    setCurrentPage(1);
  }, [tenants, searchQuery, statusFilter, sortBy]);

  useEffect(() => { fetchTenants(); }, []);
  useEffect(() => { filterAndSortTenants(); calculateStats(); }, [filterAndSortTenants, calculateStats]);

  const toggleTenantStatus = async (tenantId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
      const token = localStorage.getItem('token');
      await api.patch(`/api/auth/superadmin/tenants/${tenantId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(prev => prev.map(t => t._id === tenantId ? { ...t, status: newStatus } : t));
    } catch (err) { alert('Failed to update status.'); }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenant) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/auth/superadmin/tenants/${selectedTenant._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenants(prev => prev.filter(t => t._id !== selectedTenant._id));
      setShowDeleteModal(false);
      setSelectedTenant(null);
    } catch (err) { alert('Failed to delete tenant.'); }
  };

  const refreshData = () => { setLoading(true); fetchTenants(); };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTenants.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTenants.length / itemsPerPage);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-50 text-green-700 border-green-200';
      case 'disabled': return 'bg-red-50 text-red-700 border-red-200';
      case 'suspended': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center font-medium">Loading tenants...</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header & Stats */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Tenants Management</h1>
          <button onClick={refreshData} className="p-2 hover:bg-gray-200 rounded-lg"><RefreshCw className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl border border-gray-200 flex items-center justify-between">
            <div><p className="text-sm text-gray-500">Total</p><p className="text-2xl font-bold">{stats.total}</p></div>
            <Building2 className="text-blue-500" />
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 flex items-center justify-between">
            <div><p className="text-sm text-gray-500">Active</p><p className="text-2xl font-bold text-green-600">{stats.active}</p></div>
            <CheckCircle className="text-green-500" />
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 flex items-center justify-between">
            <div><p className="text-sm text-gray-500">Disabled</p><p className="text-2xl font-bold text-red-600">{stats.disabled}</p></div>
            <XCircle className="text-red-500" />
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 flex items-center justify-between">
            <div><p className="text-sm text-gray-500">Suspended</p><p className="text-2xl font-bold text-yellow-600">{stats.suspended}</p></div>
            <AlertCircle className="text-yellow-500" />
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search..." className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex gap-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
              <option value="all">All Status</option><option value="active">Active</option><option value="disabled">Disabled</option>
            </select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
              <option value="newest">Newest</option><option value="name-asc">A-Z</option>
            </select>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentItems.map(tenant => (
            <div key={tenant._id} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">{tenant.organizationName?.charAt(0)}</div>
                  <h3 className="font-semibold text-gray-900">{tenant.organizationName}</h3>
                </div>
                <div className="relative group">
                   <button onClick={() => setSelectedTenant(tenant)} className="p-1 hover:bg-gray-100 rounded"><MoreVertical className="w-4 h-4 text-gray-400" /></button>
                   {selectedTenant?._id === tenant._id && (
                     <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-xl z-20 py-1">
                        <button onClick={() => setShowDetailsModal(true)} className="w-full px-4 py-2 text-left text-sm flex items-center hover:bg-gray-50"><Eye className="w-4 h-4 mr-2" /> Details</button>
                        <button onClick={() => window.open(`https://${tenant.subdomain}.truyon.com`)} className="w-full px-4 py-2 text-left text-sm flex items-center hover:bg-gray-50"><ExternalLink className="w-4 h-4 mr-2" /> Visit</button>
                        <button onClick={() => setShowDeleteModal(true)} className="w-full px-4 py-2 text-left text-sm flex items-center text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-2" /> Delete</button>
                     </div>
                   )}
                </div>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center"><Mail className="w-4 h-4 mr-2" /> {tenant.email}</div>
                <div className="flex items-center"><Globe className="w-4 h-4 mr-2" /> {tenant.subdomain}.truyon.com</div>
                <div className="flex items-center"><Calendar className="w-4 h-4 mr-2" /> Created: {new Date(tenant.createdAt?._seconds * 1000).toLocaleDateString()}</div>
              </div>
              <div className="mt-4 pt-4 border-t flex justify-between items-center">
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(tenant.status)}`}>{tenant.status}</span>
                <button onClick={() => toggleTenantStatus(tenant._id, tenant.status)} className="text-sm font-medium text-blue-600">
                  {tenant.status === 'active' ? 'Disable' : 'Enable'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-between items-center bg-white p-4 rounded-xl border border-gray-200">
            <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
            <div className="flex space-x-2">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-xl max-w-sm w-full text-center">
            <Trash2 className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold">Delete {selectedTenant?.organizationName}?</h3>
            <p className="text-sm text-gray-500 my-4">This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
              <button onClick={handleDeleteTenant} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-xl max-w-lg w-full">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
              <h2 className="text-xl font-bold">Tenant Information</h2>
              <button onClick={() => setShowDetailsModal(false)}><XCircle className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              <div><p className="text-xs text-gray-500">Organization</p><p className="font-medium">{selectedTenant.organizationName}</p></div>
              <div><p className="text-xs text-gray-500">Subdomain</p><p className="font-medium">{selectedTenant.subdomain}.truyon.com</p></div>
              <div><p className="text-xs text-gray-500">Admin Email</p><p className="font-medium">{selectedTenant.email}</p></div>
              <div><p className="text-xs text-gray-500">Full ID</p><p className="font-mono text-xs">{selectedTenant._id}</p></div>
            </div>
            <button onClick={() => setShowDetailsModal(false)} className="mt-6 w-full py-2 bg-blue-600 text-white rounded-lg">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tenants;